import { Check, X } from 'lucide-react';
import type React from 'react';
import { getDocumentIcon } from '@/lib/document-icons';
import { cn } from '@/lib/utils';
import type { DocumentSelection } from '@/types';

interface SelectedDocumentsListProps {
  documentSelections: Map<string, DocumentSelection>;
  showAll: boolean;
  onShowAll: () => void;
  /** Maximum documents to show before "N more" button (2 for welcome, 3 for conversation) */
  maxVisible: number;
  onOpenDocument: (guid: string, e: React.MouseEvent) => void;
  onRemoveGroup: (guid: string) => void;
  onToggleParent: (guid: string) => void;
  onToggleAttachment: (parentGuid: string, attachGuid: string) => void;
  /** Clear all selected documents from the conversation */
  onClearAll?: () => void;
}

/**
 * Renders the list of selected documents with parent/attachment toggles
 * Used in ChatInputArea for both welcome and conversation views.
 */
export const SelectedDocumentsList: React.FC<SelectedDocumentsListProps> = ({
  documentSelections,
  showAll,
  onShowAll,
  maxVisible,
  onOpenDocument,
  onRemoveGroup,
  onToggleParent,
  onToggleAttachment,
  onClearAll,
}) => {
  if (documentSelections.size === 0) return null;

  const entries = Array.from(documentSelections.entries());
  const shouldCollapse = !showAll && documentSelections.size > maxVisible;
  const visibleEntries = shouldCollapse ? entries.slice(0, maxVisible) : entries;

  return (
    <div className="flex-1 overflow-hidden">
      <div
        className={cn(
          'overflow-y-auto',
          showAll || documentSelections.size <= maxVisible ? 'max-h-32' : ''
        )}
      >
        <div className="flex flex-wrap items-start gap-1.5 py-0.5">
          {visibleEntries.map(([docGuid, selection]) => {
            const hasAttachments = selection.attachments.length > 0;
            const isParentIncluded = selection.include_parent;
            const parentIconInfo = getDocumentIcon(selection.fileName || '', hasAttachments);
            const ParentIcon = parentIconInfo.Icon;

            return (
              <div
                key={docGuid}
                className={cn(
                  'flex min-w-0 max-w-full flex-col items-start gap-1',
                  hasAttachments && 'basis-full'
                )}
              >
                {/* Parent document */}
                <div
                  className={cn(
                    'group flex h-[26px] min-w-0 max-w-full items-center rounded-lg border pl-1.5 pr-1 text-xs font-medium transition-colors duration-200',
                    !isParentIncluded
                      ? 'border-slate-200/90 bg-white/70 text-slate-400'
                      : 'border-[#dce2f3] bg-[#f3f6fc] text-[#4b556f] hover:border-[#ccd7ee] hover:bg-[#eef2fa]'
                  )}
                >
                  {/* Checkbox to toggle parent inclusion - only show for documents with attachments */}
                  {hasAttachments && (
                    <button
                      type="button"
                      aria-label={!isParentIncluded ? 'Include document' : 'Exclude document'}
                      className={cn(
                        'mr-1.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-[4px] border transition-all focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-[#b9c6e7] focus-visible:ring-offset-1',
                        !isParentIncluded
                          ? 'border-slate-300 bg-white hover:border-[#8FA0D0]'
                          : 'border-[#8FA0D0] bg-[#8FA0D0] shadow-[0_1px_3px_rgba(95,112,168,0.25)] hover:bg-[#7F91C4]'
                      )}
                      onClick={() => onToggleParent(docGuid)}
                      title={!isParentIncluded ? 'Include document' : 'Exclude document'}
                    >
                      {isParentIncluded && (
                        <Check
                          className="h-3 w-3 text-white"
                          strokeWidth={3}
                          aria-hidden="true"
                        />
                      )}
                    </button>
                  )}
                  <button
                    type="button"
                    className="flex min-w-0 flex-1 items-center rounded-sm pr-1 text-left transition-colors hover:text-slate-900 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-[#b9c6e7]"
                    onClick={(e) => onOpenDocument(docGuid, e)}
                  >
                    <ParentIcon
                      className={cn(
                        'mr-1.5 h-3.5 w-3.5 shrink-0',
                        !isParentIncluded ? 'text-slate-400' : parentIconInfo.colorClass
                      )}
                    />
                    <span className={cn('min-w-0 truncate', !isParentIncluded && 'line-through')}>
                      {selection.displayName || selection.fileName}
                    </span>
                  </button>

                  {/* X button to remove entire group */}
                  <button
                    type="button"
                    aria-label={hasAttachments ? 'Remove email and attachments' : 'Remove document'}
                    className="ml-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-sm text-slate-400 transition-colors hover:bg-white hover:text-[#5F6C96] focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-[#b9c6e7]"
                    onClick={() => onRemoveGroup(docGuid)}
                    title={hasAttachments ? 'Remove email and attachments' : 'Remove document'}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>

                {/* Attachments */}
                {hasAttachments && (
                  <div className="ml-5 flex max-w-[calc(100%_-_1.25rem)] flex-col gap-1 border-l border-[#dfe6f2] pl-2">
                    {selection.attachments.map((attachment) => {
                      const isIncluded = attachment.included;
                      const attachIconInfo = getDocumentIcon(attachment.filename || '', attachment.hasAttachments);
                      const AttachIcon = attachIconInfo.Icon;
                      return (
                        <div
                          key={attachment.guid}
                          className={cn(
                            'flex h-6 min-w-0 max-w-full items-center rounded-lg border px-1.5 text-[11px] font-medium transition-colors',
                            !isIncluded
                              ? 'border-slate-200/80 bg-white/60 text-slate-400'
                              : 'border-[#dce2f3] bg-[#f8fafd] text-[#4b556f] hover:border-[#ccd7ee] hover:bg-[#f3f6fc]'
                          )}
                        >
                          {/* Checkbox for attachment */}
                          <button
                            type="button"
                            aria-label={!isIncluded ? 'Include attachment' : 'Exclude attachment'}
                            className={cn(
                              'mr-1.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-[4px] border transition-all focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-[#b9c6e7] focus-visible:ring-offset-1',
                              !isIncluded
                                ? 'border-slate-300 bg-white hover:border-[#8FA0D0]'
                                : 'border-[#8FA0D0] bg-[#8FA0D0] hover:bg-[#7F91C4]'
                            )}
                            onClick={() => onToggleAttachment(docGuid, attachment.guid)}
                            title={!isIncluded ? 'Include attachment' : 'Exclude attachment'}
                          >
                            {isIncluded && (
                              <Check
                                className="h-3 w-3 text-white"
                                strokeWidth={3}
                                aria-hidden="true"
                              />
                            )}
                          </button>
                          <button
                            type="button"
                            className="flex min-w-0 flex-1 items-center rounded-sm text-left transition-colors hover:text-slate-900 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-[#b9c6e7]"
                            onClick={(e) => onOpenDocument(attachment.guid, e)}
                          >
                            <AttachIcon
                              className={cn(
                                'mr-1 h-3.5 w-3.5 shrink-0',
                                !isIncluded ? 'text-slate-400' : attachIconInfo.colorClass
                              )}
                            />
                            <span className={cn('truncate min-w-0', !isIncluded && 'line-through')}>
                              {attachment.filename}
                            </span>
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}

          {/* Show more button */}
          {shouldCollapse && (
            <button
              type="button"
              className="flex h-[26px] items-center self-start rounded-lg bg-white px-3 text-[11px] font-medium text-[#69768e] ring-1 ring-slate-200/80 transition-colors hover:text-slate-800 hover:ring-slate-300 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-[#b9c6e7] focus-visible:ring-offset-1"
              onClick={onShowAll}
            >
              <span>+{documentSelections.size - maxVisible} more</span>
            </button>
          )}

          {onClearAll && documentSelections.size > maxVisible && (
            <button
              type="button"
              className="flex h-[26px] items-center self-start rounded-lg bg-white px-3 text-[11px] font-medium text-[#69768e] ring-1 ring-slate-200/80 transition-colors hover:text-slate-800 hover:ring-slate-300 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-[#b9c6e7] focus-visible:ring-offset-1"
              onClick={onClearAll}
            >
              Clear All
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SelectedDocumentsList;
